import React, { useRef } from 'react'
import { Badge, Spinner } from 'react-bootstrap'
import styles from './DomainCell.module.css'
import CustomTooltip from '../../../CustomTooltip'
import useCheckIsMobileScreen from '../../../../hooks/checkMobileScreen'

const getStatusDot = ({ status, statusText, passwordProtected, emailProtected }) => {
  if (passwordProtected || emailProtected) return { variant: 'private', label: 'Private' }
  if (statusText === 'Archived' || status === 'archived') return { variant: 'archived', label: 'Archived' }
  if (statusText === 'Deleted' || status === 'deleted') return { variant: 'error', label: 'Deleted' }
  if (status === 'processing') return { variant: 'processing', label: 'Processing' }
  if (status === 'error' || status === 'failed' || status === 'danger' || status === 'disabled') {
    return { variant: 'error', label: 'Error' }
  }
  if (status === 'preview') return { variant: 'preview', label: 'Preview' }
  if (status === 'pending' || status === 'incomplete') return { variant: 'pending', label: 'Pending' }
  if (status === 'initiated') return { variant: 'initiated', label: 'Initiated' }
  return { variant: 'public', label: 'Public' }
}

const StatusDot = ({ status, statusText, passwordProtected, emailProtected }) => {
  const { variant, label } = getStatusDot({ status, statusText, passwordProtected, emailProtected })

  if (variant === 'processing') {
    return (
      <Spinner
        className={styles.processingSpinner}
        animation="grow"
        variant="secondary"
        size="sm"
        role="img"
        aria-label={label}
      />
    )
  }

  return (
    <span
      className={`${styles.statusDot} ${styles[`${variant}Dot`]}`}
      title={label}
      aria-label={label}
      role="img"
      data-testid="DomainCell-status-dot"
    />
  )
}

const getProjectTypeIcon = (site) => {
  const siteType = site?.siteType || site?.linkRecord?.siteType
  const currentType = site?.type || site?.linkRecord?.type
  if (siteType?.toLowerCase()?.includes('github') || site?.githubRepo || site?.repository) {
    return {
      src: '/assets/icons/github.svg',
      alt: 'GitHub repository'
    }
  }

  if (siteType?.includes('multiple') && !currentType?.toLowerCase()?.includes('video')) {
    return {
      src: '/assets/icons/folder.svg',
      alt: 'Multiple page project'
    }
  }

  let fileType = site?.type?.toLowerCase()

  if (!fileType && site?.linkRecord) {
    fileType = site.linkRecord.type?.toLowerCase()
  }

  if (!fileType) {
    const filename = site?.filename || site?.linkRecord?.filename || site?.linkRecord?.name || ''
    fileType = filename.split('.').pop()?.toLowerCase()
  }

  switch (fileType) {
    case 'pdf':
    case 'application/pdf':
      return {
        src: '/assets/icons/pdfIcon.svg',
        alt: 'PDF document'
      }

    case 'php':
    case 'text/php':
    case 'application/x-httpd-php':
      return {
        src: '/assets/icons/phpIcon.svg',
        alt: 'PHP application'
      }

    case 'image':
    case 'image/jpeg':
    case 'image/jpg':
    case 'jpg':
    case 'jpeg':
    case 'image/png':
    case 'png':
    case 'image/gif':
    case 'gif':
    case 'image/bmp':
    case 'bmp':
    case 'image/webp':
    case 'webp':
    case 'image/svg+xml':
    case 'svg':
      return {
        src: '/assets/icons/photo.svg',
        alt: 'Image file'
      }

    case 'video':
    case 'video/mp4':
    case 'mp4':
    case 'video/mov':
    case 'mov':
    case 'video/avi':
    case 'avi':
    case 'video/webm':
    case 'webm':
    case 'video/quicktime':
      return {
        src: '/assets/icons/videoIcon.svg',
        alt: 'Video file'
      }

    case 'audio':
    case 'audio/mp3':
    case 'mp3':
    case 'audio/wav':
    case 'wav':
    case 'audio/ogg':
    case 'ogg':
    case 'audio/aac':
    case 'aac':
    case 'audio/mpeg':
      return {
        src: '/assets/icons/soundIcon.svg',
        alt: 'Audio file'
      }

    case 'code':
      return {
        src: '/assets/icons/code-bracket-icon.svg',
        alt: 'Code file'
      }

    case 'html':
    case 'htm':
    case 'text/html':
    case 'zip':
    case 'application/zip':
      return {
        src: '/assets/icons/document-code-icon.svg',
        alt: 'Web project'
      }

    default:
      return {
        src: '/assets/icons/document-text.svg',
        alt: 'Document'
      }
  }
}

const DomainCell = ({
  label,
  href,
  site,
  handlePreviewClick,
  disabled,
  trialAccount,
  onShare,
  isTransferRequestLink,
  daysRemaining,
  showSubmitGame,
  onSubmitGame,
  gameShowcaseStatus,
  status,
  statusText,
  passwordProtected,
  emailProtected
}) => {
  const copyBtnRef = useRef(null)
  const { isMobile, isTablet } = useCheckIsMobileScreen()
  const isDesktop = !isMobile && !isTablet
  const onDomainClick = (e) => {
    if (disabled) return undefined

    e.preventDefault()
    if (site?.inPreviewMode) {
      handlePreviewClick(site)
    } else {
      const link = trialAccount ? `${href.replace(/\/$/, '')}${trialAccount ? '?mode=suggestions' : ''}` : href
      window.open(link, '_blank')
    }
  }

  const onCopy = () => {
    onShare(site)
  }

  const updatedLabel = site?.inPreviewMode ? 'Click to preview' : (label ?? '')
  const displayLabel = isMobile && updatedLabel.length > 16 ? `${updatedLabel.slice(0, 16)}...` : updatedLabel
  const projectTypeIcon = getProjectTypeIcon(site)
  return (
    <div className={styles.container}>
      <a
        onClick={onDomainClick}
        className={`domain-cell ${styles.link} ${disabled ? styles.disabled : ''}`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="DomainCell-a-domain-cell"
      >
        <div className={styles.contentWrapper}>
          {isMobile ? (
            <StatusDot
              status={status ?? site?.linkStatus}
              statusText={statusText}
              passwordProtected={passwordProtected ?? site?.passwordProtected}
              emailProtected={emailProtected ?? site?.enableEmailWhitelist}
            />
          ) : (
            <img
              src={projectTypeIcon.src}
              alt={projectTypeIcon.alt}
              className={projectTypeIcon.isLogo ? styles.logoIcon : styles.icon}
            />
          )}
          {isTransferRequestLink && (
            <img src="/assets/icons/transfer-request-link.svg" alt="Transfer Request Link" className={styles.icon} />
          )}

          <div className={styles.text}>
            {displayLabel}
            {disabled && typeof daysRemaining === 'number' && daysRemaining >= 0 && (
              <span className={`text-danger small ${styles.daysRemaining}`}>
                ({daysRemaining} {daysRemaining === 1 ? 'day' : 'days'})
              </span>
            )}
            {(() => {
              const siteType = site?.siteType || site?.linkRecord?.siteType
              const isGitHubProject =
                siteType?.toLowerCase()?.includes('github') || site?.githubRepo || site?.repository

              return (
                isGitHubProject &&
                site?.githubRepo && (
                  <div className="text-muted small mt-1">
                    <a
                      href={site.githubRepo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-none"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {site.githubRepo}
                    </a>
                  </div>
                )
              )
            })()}
          </div>
        </div>
      </a>

      <div ref={copyBtnRef} className={styles.actions}>
        {!site?.inPreviewMode && !disabled && (
          <CustomTooltip label="Copy link">
            <button
              className={`${styles.copyLinkBtn} copy-domain-btn`}
              onClick={() => onCopy(href)}
              disabled={disabled}
            >
              <img className="op-6" height="16" src="/assets/icons/copy-link.svg" alt="tooltip" />
            </button>
          </CustomTooltip>
        )}
      </div>
      {isDesktop &&
        showSubmitGame &&
        !site?.inPreviewMode &&
        !disabled &&
        (gameShowcaseStatus ? (
          <span
            className={`${styles.gameStatusBadge} ${gameShowcaseStatus === 'approved' ? styles.gameStatusApproved : styles.gameStatusPending}`}
            data-testid="DomainCell-badge-game-showcase-status"
          >
            <i
              className={`bi ${gameShowcaseStatus === 'approved' ? 'bi-check-circle-fill' : 'bi-hourglass-split'}`}
              aria-hidden="true"
            />
            {gameShowcaseStatus === 'approved' ? 'Live in showcase' : 'Pending review'}
          </span>
        ) : (
          <button
            type="button"
            className={styles.submitGameBtn}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onSubmitGame?.(site)
            }}
            data-testid="DomainCell-button-submit-game"
          >
            <Badge className={styles.submitGameBadge}>
              <i className="bi bi-controller" aria-hidden="true" />
            </Badge>
            Submit Game
          </button>
        ))}
    </div>
  )
}

export default DomainCell
